import { DropdownOption } from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import styled from 'styled-components';
import { Header, RadioProps } from 'semantic-ui-react';
import { RouteComponentProps } from 'react-router';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { DocumentCopiesSearchRequest, DocumentCopiesSearchResponse, GuarantorDto } from 'ts-types/api.types';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { BillSearchBy, DocumentCopiesSearchBy } from 'ts-types/api.enums';
import moment from 'moment';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { isValueEmpty } from 'utils/validatorUtils';
import { FormApi, FormState } from 'final-form';
import { debounce } from 'ts-debounce';
import { searchCollectiveBills } from 'services/documentCopiesServices';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { noop } from 'utils/functionUtils';
import React, { useEffect, useState } from 'react';
import Radio from 'components/final-form/Radio';
import Input from 'components/final-form/Input';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import DatePicker from 'components/final-form/DatePicker';
import { getAllGuarantorsForCollectiveBillTrue } from 'services/guarantorServices';
import Select from 'components/final-form/Select';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 1em 1em;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

   flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }
`;

const StyledHeader = styled(Header)`
  margin-bottom: 1em !important;
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 1 1 20%;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

interface Props extends RouteComponentProps<any>, MessageSourceProps, AuthConsumerRenderProps {
  onFilterBills: (documents: Array<DocumentCopiesSearchResponse>) => void,
  onBillsLoadingChange: (documentsLoading: boolean) => void,
  onSearchedId: (id: number | undefined) => void,
  onExpandedBill: (bill: any) => void
}

interface ExtendedDocumentCopiesSearchRequest extends DocumentCopiesSearchRequest {
  billFromDate: string,
  billToDate: string,
  reminderFromDate: string,
  reminderToDate: string,
  guarantorId: number,
  onlyCollectiveBills: boolean
}

let initialValues: Partial<ExtendedDocumentCopiesSearchRequest> = {
  searchBy: BillSearchBy.BILL_NUMBER,
  billFromDate: moment().format("YYYY-MM-DD"),
  billToDate: moment().format("YYYY-MM-DD"),
  reminderFromDate: moment().format("YYYY-MM-DD"),
  reminderToDate: moment().format("YYYY-MM-DD")
}

const radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

const CollectiveBillsSearchSegment = (props: Props) => {

  const {currentUser, onBillsLoadingChange, onFilterBills, onSearchedId, onExpandedBill, getMessage} = props;
  const searchByValues = Object.values(DocumentCopiesSearchBy);
  const radioDefinitions = generateRadioButtonOptionsFromEnum(searchByValues, "searchBy", getMessage);

  const [guarantors, setGuarantors] = useState([] as DropdownOption[]);

  useEffect(() => {

    const methods: Array<Promise<any>> = [
      getAllGuarantorsForCollectiveBillTrue(cancelTokenSource)
    ];

    Promise.all(methods)
        .then(responses => {

          const guarantorsWithCollectiveBills: Array<GuarantorDto> = responses[0];

          const mappedGuarantors: Array<DropdownOption> = guarantorsWithCollectiveBills.map(guarantor => ({
            key: guarantor.id,
            text: `${guarantor.description}, ${guarantor.zip}, ${guarantor.city}`,
            value: guarantor.id
          }));

          setGuarantors(mappedGuarantors);

        })
        .catch(noop);

  }, []);

  radioDefinitions.forEach((radioDefinition, index) => {
    radioMap.set(searchByValues[index], radioDefinition);
  });

  initialValues.mandateId = currentUser ? currentUser.userConfig.mandateId : undefined;

  const shouldSearchBills = (values: Partial<ExtendedDocumentCopiesSearchRequest>): boolean => {

    const {searchBy, fromBillNumber, toBillNumber, fromAmount, toAmount, guarantorId} = values;

    switch (searchBy) {
      case DocumentCopiesSearchBy.BILL_NUMBER: {
        return !isValueEmpty(fromBillNumber) || !isValueEmpty(toBillNumber);
      }
      case DocumentCopiesSearchBy.AMOUNT: {
        return !isValueEmpty(fromAmount) || !isValueEmpty(toAmount);
      }
      case DocumentCopiesSearchBy.BILL_GUARANTOR: {
        return !isValueEmpty(guarantorId);
      }
      default: {
        return true;
      }
    }
  };

  const handleChange = ({values}: FormState, form: FormApi) => {

    onExpandedBill(undefined);

    const billFromDate = values.billFromDate && new Date(values.billFromDate);
    const billToDate = values.billToDate && new Date(values.billToDate);
    const reminderFromDate = values.reminderFromDate && new Date(values.reminderFromDate);
    const reminderToDate = values.reminderToDate && new Date(values.reminderToDate);

    if (shouldSearchBills(values)) {
      onBillsLoadingChange(true);
      searchCollectionBills(values);
    }

    if (billFromDate && billToDate && billFromDate.getTime() > billToDate.getTime()) {
      form.change("billToDate", moment(billFromDate).format("YYYY-MM-DD"));
    }
    if (reminderFromDate && reminderToDate && reminderFromDate.getTime() > reminderToDate.getTime()) {
      form.change("reminderToDate", moment(reminderFromDate).format("YYYY-MM-DD"));
    }
  };

  const searchCollectionBills = debounce((values: Partial<ExtendedDocumentCopiesSearchRequest>) => {

    onBillsLoadingChange(true);

    let request: Partial<ExtendedDocumentCopiesSearchRequest> = {
      ...values
    }
    if (values.searchBy === DocumentCopiesSearchBy.BILL_DATE) {
      request.fromDate = values.billFromDate;
      request.toDate = values.billToDate;
    }
    if (values.searchBy === DocumentCopiesSearchBy.REMINDER_DATE) {
      request.fromDate = values.reminderFromDate;
      request.toDate = values.reminderToDate;
    }

    request.onlyCollectiveBills = values.searchBy !== DocumentCopiesSearchBy.BILL_NUMBER;

    if (values.searchBy === DocumentCopiesSearchBy.BILL_NUMBER) {
      const id = values.fromBillNumber ? values.fromBillNumber : values.toBillNumber;

      onSearchedId(id);
    } else {
      onSearchedId(undefined);
    }

    searchCollectiveBills(request, cancelTokenSource)
        .then(response => {
          onFilterBills(response);
        })
        .finally(() => {
          onBillsLoadingChange(false);
        })
  },400);

  const renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const billNumberRadioDefinition = radioMap.get(DocumentCopiesSearchBy.BILL_NUMBER);
    const guarantorRadioDefinition = radioMap.get(DocumentCopiesSearchBy.BILL_GUARANTOR);
    const amountRadioDefinition = radioMap.get(DocumentCopiesSearchBy.AMOUNT);
    const billDateRadioDefinition = radioMap.get(DocumentCopiesSearchBy.BILL_DATE);
    const reminderDateRadioDefinition = radioMap.get(DocumentCopiesSearchBy.REMINDER_DATE);

    const disabledBillBefore = values.billFromDate && new Date(values.billFromDate);
    const disabledReminderBefore = values.reminderFromDate && new Date(values.reminderFromDate);

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <StyledHeader>{getMessage("searchOptions")}</StyledHeader>
          </StyledContainer>
          <StyledContainer>
            <SearchOptionsContainer>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={billNumberRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="fromBillNumber"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("fromBillNumber")}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="toBillNumber"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("toBillNumber")}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={guarantorRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="guarantorId"
                      component={Select}
                      options={guarantors}
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={amountRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromAmount"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("fromAmount")}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toAmount"
                      component={Input}
                      onKeyDown={ensureOnlyNumericInput}
                      placeholder={getMessage("toAmount")}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={billDateRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="billFromDate"
                      component={DatePicker}
                      emptyDateInput
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="billToDate"
                      component={DatePicker}
                      disabledBefore={disabledBillBefore}
                      emptyDateInput
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <SearchField>
                  <Field
                      name="searchBy"
                      component={Radio}
                      radioDefinition={reminderDateRadioDefinition}
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="reminderFromDate"
                      component={DatePicker}
                      emptyDateInput
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="reminderToDate"
                      component={DatePicker}
                      disabledBefore={disabledReminderBefore}
                      emptyDateInput
                      fluid
                  />
                </SearchField>
              </SearchGroup>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => handleChange(formState, form)}/>
        </StyledForm>
    )
  }

  return (
      <FinalForm
          onSubmit={() => noop}
          initialValues={initialValues}
          subscription={{values: true, pristine: true, submitting: true}}
          render={renderForm}
      />
  );
}

export default withRouterWorkaround(withAuthContext(withMessages(CollectiveBillsSearchSegment)));

